import { createStore, useStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type PatientListEntry = {
  truentityId: string;
  name?: string;
  path: string;
};

export type PatientListSession = {
  entries: PatientListEntry[];
  currentIndex: number;
  /** null when the position isn't known in patients, so the pager shows no count. */
  offset: number | null;
  total: number;
  returnTo: string;
};

interface PatientListNavigationState {
  session: PatientListSession | null;
  sessionId: number;
  pending: boolean;
  startSession: (session: PatientListSession, pending: boolean) => number;
  completeSession: (sessionId: number, frozen: Pick<PatientListSession, 'entries' | 'offset' | 'total'> | null) => void;
  setCurrentIndex: (index: number) => void;
  clearSession: () => void;
}

const patientListNavigationStore = createStore<PatientListNavigationState>()(
  persist(
    (set, get) => ({
      session: null,
      sessionId: 0,
      pending: false,
      startSession: (session, pending) => {
        const sessionId = get().sessionId + 1;
        set({ session, sessionId, pending });
        return sessionId;
      },
      completeSession: (sessionId, frozen) => {
        const { session, sessionId: currentId } = get();
        if (sessionId !== currentId || !session) return;
        if (!frozen) return set({ pending: false });

        const currentPatientId = session.entries[session.currentIndex]?.truentityId;
        const currentIndex = frozen.entries.findIndex(entry => entry.truentityId === currentPatientId);
        if (currentIndex < 0) return set({ pending: false });

        set({ session: { ...session, ...frozen, currentIndex }, pending: false });
      },
      setCurrentIndex: index => set(state => (state.session ? { session: { ...state.session, currentIndex: index } } : {})),
      clearSession: () => set({ session: null, pending: false })
    }),
    {
      name: 'patientListNavigation',
      storage: createJSONStorage(() => sessionStorage),
      partialize: ({ session, sessionId }) => ({
        sessionId,
        session: session && { ...session, entries: session.entries.map(({ truentityId, path }) => ({ truentityId, path })) }
      })
    }
  )
);

export const getPatientListNavigationState = patientListNavigationStore.getState;

export function usePatientListNavigationStore(): PatientListNavigationState;
// eslint-disable-next-line no-redeclare
export function usePatientListNavigationStore<T>(selector: (state: PatientListNavigationState) => T, equals?: (a: T, b: T) => boolean): T;
// eslint-disable-next-line no-redeclare
export function usePatientListNavigationStore<T>(selector?: (state: PatientListNavigationState) => T, equals?: (a: T, b: T) => boolean) {
  return useStore(patientListNavigationStore, selector!, equals);
}
